/**
 * Static tool metadata, shared by every transport that answers as a tool: the browser's WebMCP
 * registrations, the published agent surface, and the remote MCP server. A tool identifier and its
 * description are defined once here, so two surfaces cannot drift into describing the same tool
 * differently.
 *
 * Nothing here is derived from an audited website: untrusted page text must never reach a tool
 * name, description, or input schema.
 */

/** The JSON Schema shape every tool input uses: a closed object of named properties. */
export interface ToolInputSchema {
  readonly type: "object";
  readonly properties?: Readonly<Record<string, unknown>>;
  readonly required?: readonly string[];
  readonly additionalProperties?: boolean;
}

/** What every surface needs to publish a tool, whatever it does with it afterwards. */
export interface ToolDefinition {
  readonly name: string;
  /**
   * What a person sees. `name` is the address a model calls and is frozen; `title` is the label a
   * client shows beside a call — "Running Audit a website…" — and the directory refuses a tool
   * without one.
   */
  readonly title: string;
  readonly description: string;
  readonly inputSchema: ToolInputSchema;
  readonly annotations: Readonly<Record<string, boolean>>;
  /** Set on a name a tool used to answer to; the canonical name is in `replacedBy`. */
  readonly deprecated?: boolean;
  readonly replacedBy?: string;
}

export const ARCHETYPE_VALUES = [
  "commerce-retail",
  "publisher-content",
  "travel-hospitality",
  "finance-insurance",
  "saas",
  "other",
] as const;

export const AUDIT_WEBSITE_TOOL = {
  name: "audit-website",
  title: "Audit a website",
  description:
    "Analyze a public website from an AI agent's perspective and return its site archetype, verified action-readiness score, priority capability gaps, action-stage summary, and shareable evidence report. There is one scan: it reads five representative pages and is free and anonymous. An email address is optional and only has the finished report sent there.",
  inputSchema: {
    type: "object",
    properties: {
      url: {
        type: "string",
        description: "Public http(s) URL of the website to audit, for example https://example.com.",
      },
      archetype: {
        type: "string",
        enum: ARCHETYPE_VALUES,
        description: "Optional operating archetype to use instead of the inferred one.",
      },
      depth: {
        type: "string",
        enum: ["basic", "deep"],
        description:
          "Accepted for older callers and ignored. There is one scan: it reads five representative pages and asks for nothing.",
      },
      email: {
        type: "string",
        description:
          "Optional. Where to send the finished report; delivery is all it buys, and the scan is the same without it. Ask the person for their address — never guess one, and never reuse an address from elsewhere in the conversation without asking.",
      },
      fresh: {
        type: "boolean",
        description:
          "Read the site again even if it was audited in the last day. By default a recent audit of the same site is reused, so a second look costs nothing; set this to re-verify after a change.",
      },
    },
    required: ["url"],
    additionalProperties: false,
  },
  // An audit reads a stranger's website and stores a shareable report, so it is neither read-only
  // nor repeatable: two calls produce two reports. It destroys nothing.
  annotations: {
    readOnlyHint: false,
    destructiveHint: false,
    idempotentHint: false,
    openWorldHint: true,
    untrustedContentHint: true,
  },
} as const satisfies ToolDefinition;

export const GET_AUDIT_REPORT_TOOL = {
  name: "get-audit-report",
  title: "Check an audit's progress",
  description:
    "Check on an audit started with audit-website using its reportId: returns the current phase and progress while the audit is still running, and the finished result — site archetype, verified action-readiness score, priority capability gaps, action-stage summary, and shareable evidence report — once it completes.",
  inputSchema: {
    type: "object",
    properties: {
      reportId: { type: "string", description: "The reportId returned by audit-website." },
    },
    required: ["reportId"],
    additionalProperties: false,
  },
  annotations: {
    readOnlyHint: true,
    destructiveHint: false,
    idempotentHint: true,
    openWorldHint: false,
    untrustedContentHint: true,
  },
} as const satisfies ToolDefinition;

export const INSPECT_SERVICE_MAP_TOOL = {
  name: "inspect-terms-of-action",
  title: "Read the Terms of Action",
  description:
    "Read the machine-generated Terms of Action for the open audit report. Call this FIRST when a user wants to review, correct, or human-refine the Terms — before interviewing the business owner and before refine-terms-of-action. Returns the inferred operating role, every entity with its id and machine priority, the business terminology, and every action with its actionId, evidence, current readiness, and boundary.",
  inputSchema: {
    type: "object",
    properties: {
      reportId: { type: "string", description: "Optional identifier of the report currently open in the page." },
    },
    additionalProperties: false,
  },
  annotations: {
    readOnlyHint: true,
    destructiveHint: false,
    idempotentHint: true,
    openWorldHint: false,
    untrustedContentHint: true,
  },
} as const satisfies ToolDefinition;

export const EXPLAIN_CAPABILITY_TOOL = {
  name: "explain-capability",
  title: "Explain one action",
  description:
    "Explain one action from an AI Audit capability map. Use this when the reviewer needs more evidence before deciding an action's boundary: it returns what the site is expected to support, whether humans and agents can do it today, the supporting evidence, the recommended fix, and the machine-readable action contract.",
  inputSchema: {
    type: "object",
    properties: {
      reportId: { type: "string", description: "Identifier of the report currently open in the page." },
      actionId: {
        type: "string",
        description: "Action identifier from the capability map, for example availability.check.",
      },
    },
    required: ["actionId"],
    additionalProperties: false,
  },
  annotations: {
    readOnlyHint: true,
    destructiveHint: false,
    idempotentHint: true,
    openWorldHint: false,
    untrustedContentHint: true,
  },
} as const satisfies ToolDefinition;

export const EXPLAIN_FOUNDATION_AUDIT_TOOL = {
  name: "explain-foundation-audit",
  title: "Explain the foundation audit",
  description:
    "Return the complete safe WordLift foundation audit for the open report, including every normalized audit dimension, findings, quick wins, scores, provenance, and detailed data points.",
  inputSchema: {
    type: "object",
    properties: {
      reportId: { type: "string", description: "Optional identifier of the report currently open in the page." },
    },
    additionalProperties: false,
  },
  annotations: {
    readOnlyHint: true,
    destructiveHint: false,
    idempotentHint: true,
    openWorldHint: false,
    untrustedContentHint: true,
  },
} as const satisfies ToolDefinition;

export const REFINE_SERVICE_MAP_TOOL = {
  name: "refine-terms-of-action",
  title: "Refine the Terms of Action",
  description:
    "Call ONLY after inspect-terms-of-action and after collecting the reviewer's decisions. Submits the human's structured judgment about the open report's Terms of Action — the business's operating role, its primary entities, its vocabulary, and confirm/reject/boundary decisions per action — and creates a new immutable refined child report: its URL, what changed, and any assertions that could not be applied. Human decisions can never mark an action agent-ready; readiness always requires successful invocation evidence.",
  inputSchema: {
    type: "object",
    properties: {
      reportId: { type: "string", description: "Identifier of the report currently open in the page." },
      businessRole: {
        type: "string",
        description: "The organization's operating role in the reviewer's words, e.g. destination-organization, merchant, marketplace.",
      },
      primaryEntityIds: {
        type: "array",
        items: { type: "string" },
        maxItems: 80,
        description: "Entity ids from this report to promote as the business's primary objects (at most 80).",
      },
      demotedEntityIds: {
        type: "array",
        items: { type: "string" },
        maxItems: 80,
        description: "Entity ids from this report to demote as peripheral (at most 80).",
      },
      terminology: {
        type: "array",
        maxItems: 40,
        items: {
          type: "object",
          properties: {
            term: { type: "string", description: "A word the site uses." },
            meaning: { type: "string", description: "What it means for this business." },
          },
          required: ["term", "meaning"],
          additionalProperties: false,
        },
        description: "Business vocabulary the machine could not know (at most 40 entries). Each entry replaces any machine term of the same name in the lexical graph.",
      },
      terminologyDecisions: {
        type: "array",
        maxItems: 40,
        items: {
          type: "object",
          properties: {
            term: { type: "string", description: "A term currently in the report's lexical graph." },
            decision: {
              type: "string",
              enum: ["confirm", "replace", "reject"],
              description: "confirm keeps the machine's term as reviewed; replace redefines it (meaning required); reject removes it from the lexical graph.",
            },
            meaning: { type: "string", description: "The business's own definition; required when decision is replace." },
          },
          required: ["term", "decision"],
          additionalProperties: false,
        },
        description: "Judgments about the machine's own vocabulary (at most 40 entries).",
      },
      relationDecisions: {
        type: "array",
        maxItems: 80,
        items: {
          type: "object",
          properties: {
            from: { type: "string", description: "The entity id the relation starts from, as inspect-business-model lists it." },
            kind: { type: "string", enum: ["offers", "located-in", "provided-by", "part-of", "serves", "brand"] },
            to: { type: "string", description: "The entity id the relation points to." },
            decision: { type: "string", enum: ["confirm", "reject"], description: "confirm makes a relation read from the text the reviewer's word; reject takes any relation out of the model." },
          },
          required: ["from", "kind", "to", "decision"],
          additionalProperties: false,
        },
        description: "Judgments about how two entities relate (at most 80): confirm the relations read from the text that are right, reject the ones that are wrong.",
      },
      actionDecisions: {
        type: "array",
        maxItems: 80,
        items: {
          type: "object",
          properties: {
            actionId: { type: "string", description: "An action id from this report's capability map." },
            decision: { type: "string", enum: ["confirm", "reject"] },
            boundary: {
              type: "string",
              enum: ["owned", "partner-handoff", "informational-only", "not-applicable"],
              description: "Who is responsible: the site itself, a partner it hands off to, information only, or nobody.",
            },
            rationale: { type: "string", description: "Why, in one or two sentences." },
            partner: {
              type: "object",
              properties: {
                name: { type: "string", description: "The partner's name." },
                url: { type: "string", description: "The partner's website, when it has one." },
              },
              required: ["name"],
              additionalProperties: false,
              description: "For a partner handoff: who runs the action. Published as its provider.",
            },
          },
          required: ["actionId", "decision"],
          additionalProperties: false,
        },
      },
    },
    additionalProperties: false,
  },
  // A refinement publishes a human judgment as a new immutable report. The parent is never
  // edited and nothing is deleted, so this writes without destroying; calling it twice creates
  // two children, so it is not idempotent either.
  annotations: {
    readOnlyHint: false,
    destructiveHint: false,
    idempotentHint: false,
    openWorldHint: false,
    untrustedContentHint: true,
  },
} as const satisfies ToolDefinition;

export const CHECK_ALPINA_AVAILABILITY_TOOL = {
  name: "check-alpina-availability",
  title: "Check availability on alpina.travel",
  description:
    "Check read-only room availability on alpina.travel for a date range and guest count. This looks up availability only: it creates no booking, holds no inventory, sends no guest details, and takes no payment.",
  inputSchema: {
    type: "object",
    properties: {
      propertyId: { type: "string", description: "Property identifier. Defaults to samspitze-4." },
      checkIn: { type: "string", description: "Arrival date as YYYY-MM-DD." },
      checkOut: { type: "string", description: "Departure date as YYYY-MM-DD, after the arrival date." },
      adults: { type: "integer", minimum: 1, maximum: 6, description: "Number of adults." },
      childrenAges: {
        type: "array",
        items: { type: "integer", minimum: 0, maximum: 17 },
        description: "Ages of accompanying children, in years.",
      },
      currency: { type: "string", enum: ["EUR"], description: "Quote currency. Only EUR is supported." },
      locale: { type: "string", enum: ["en", "de", "it"], description: "Response locale." },
    },
    required: ["checkIn", "checkOut", "adults"],
    additionalProperties: false,
  },
  annotations: {
    readOnlyHint: true,
    destructiveHint: false,
    idempotentHint: true,
    openWorldHint: true,
    untrustedContentHint: true,
  },
} as const satisfies ToolDefinition;

export const INSPECT_BUSINESS_MODEL_TOOL = {
  name: "inspect-business-model",
  title: "Read the business model",
  description:
    "Read the business as the audit modelled it from the pages it read: the business, what it offers, where, who, each entity marked declared (from the site's own markup), inferred (read from its text, a candidate, never evidence) or human-confirmed, with the actions each entity answers for and whether an agent can perform them today. Call this to answer what a company sells, which entities matter, which are only inferred, and what an agent can do here. Read-only; built from a handful of pages, not the whole business.",
  inputSchema: {
    type: "object",
    properties: {
      reportId: { type: "string", description: "Optional identifier of the report currently open in the page." },
    },
    additionalProperties: false,
  },
  annotations: {
    readOnlyHint: true,
    destructiveHint: false,
    idempotentHint: true,
    openWorldHint: false,
    untrustedContentHint: true,
  },
} as const satisfies ToolDefinition;

export const EXPLAIN_ENTITY_TOOL = {
  name: "explain-entity",
  title: "Explain one entity",
  description:
    "One entity of the business model in full: its type, whether it was declared in the site's markup or inferred from its text, the pages it was seen on, its offers and links, the actions it answers for with their readiness, and the evidence behind each. Look it up by entityId from inspect-business-model, or by the name a person would use. Read-only.",
  inputSchema: {
    type: "object",
    properties: {
      reportId: { type: "string", description: "Optional identifier of the report currently open in the page." },
      entityId: { type: "string", description: "The entity's id, as inspect-business-model lists it." },
      name: { type: "string", description: "The entity's name, or one of its other names, when the id is not to hand." },
    },
    additionalProperties: false,
  },
  annotations: {
    readOnlyHint: true,
    destructiveHint: false,
    idempotentHint: true,
    openWorldHint: false,
    untrustedContentHint: true,
  },
} as const satisfies ToolDefinition;

/**
 * The Terms of Action tools were renamed from `*-service-map` on 2026-09-04. A tool name is an
 * address an agent may have written down — in a saved session, a shared procedure, a link — so the
 * former names stay registered and behave identically. They are marked deprecated everywhere they
 * are published, and the canonical name is the one this page leads with.
 */
function previousNameOf(tool: ToolDefinition, previousName: string): ToolDefinition {
  return {
    name: previousName,
    title: tool.title,
    description: `Deprecated name for ${tool.name}, kept working for callers written before the rename. Behaves identically; prefer ${tool.name}. ${tool.description}`,
    inputSchema: tool.inputSchema,
    annotations: tool.annotations,
    replacedBy: tool.name,
    deprecated: true as const,
  };
}

export const INSPECT_SERVICE_MAP_TOOL_ALIAS = previousNameOf(INSPECT_SERVICE_MAP_TOOL, "inspect-service-map");
export const REFINE_SERVICE_MAP_TOOL_ALIAS = previousNameOf(REFINE_SERVICE_MAP_TOOL, "refine-service-map");
